import React from "react";
import { useHistory } from "react-router-dom";
import classNames from "classnames";
import { useBreakpoint } from "../../hooks";
import { Button } from "../../lib/Button";
import { Icon, IconGlyph } from "../../lib/Icon";
import { useDetermineImageClass } from "../../util";

interface IFilmstripProps {
  initialIndex: number;
  selectedAsset?: IAsset;
  galleryAssets: IAsset[];
  getNextAsset: Function;
  getPreviousAsset: Function;
  hasPreviousAsset: boolean;
  hasNextAsset: boolean;
  disableThumbnails?: boolean;
}

export const Filmstrip = ({
  galleryAssets,
  initialIndex,
  selectedAsset,
  getNextAsset,
  getPreviousAsset,
  hasPreviousAsset,
  hasNextAsset,
  disableThumbnails,
}: IFilmstripProps): JSX.Element => {
  let history = useHistory();
  let { isMobile, isDesktop } = useBreakpoint();

  // determine responsive filmstrip length based on window width
  let responsiveSizes = isMobile ? 1 : 5;
  let size = isDesktop ? 7 : responsiveSizes;

  // focus on selected index, of correct segment within gallery assets
  const buildFilmstrip = () => {
    const assets = galleryAssets.slice();
    const startIndex = Math.min(
      Math.max(initialIndex - Math.floor(size / 2), 0),
      size > assets.length ? 0 : assets.length - size
    );
    return {
      segment: assets.slice(startIndex, startIndex + size),
      selected: assets[initialIndex],
    };
  };

  const navigateRight = () => {
    if (getNextAsset) {
      let nextId = getNextAsset()?.id;
      nextId && history.push(nextId);
    }
  };

  const navigateLeft = () => {
    if (getPreviousAsset) {
      let prevId = getPreviousAsset()?.id;
      prevId && history.push(prevId);
    }
  };

  const navigateToAsset = (id: string): void => {
    if (id) {
      history.push(id);
    }
  };

  const filmstripThumbnailStyles = (asset: IAsset): string => {
    return classNames(
      "detail__filmstrip-thumb",
      asset.id === selectedAsset?.id
        ? "detail__filmstrip-thumb--active"
        : false,
      disableThumbnails ? "detail__filmstrip-thumb--disabled" : false
    );
  };

  return (
    <div className="detail__filmstrip--wrapper">
      <div className="detail__filmstrip--wrapper--inner">
        <Button
          className="detail__filmstrip-nav web-gallery-previous-arrow-pendo-tracking"
          onClick={navigateLeft}
          disabled={!hasPreviousAsset || disableThumbnails}
          aria-label="Navigate to Previous Asset"
        >
          <Icon glyph={IconGlyph.ANGLE_LEFT} />
        </Button>
        <div className="detail__filmstrip">
          {buildFilmstrip().segment?.map((asset) => {
            const imageClass = useDetermineImageClass(asset);
            return (
              <div
                key={asset.id}
                className={filmstripThumbnailStyles(asset)}
                onClick={() => navigateToAsset(asset.id)}
              >
                <div className="detail__filmstrip-buffer">
                  <img
                    className={imageClass}
                    src={asset.media.thumb}
                    alt={asset.title}
                  />
                  {asset.media.type === "Video" && (
                    <Icon
                      className="detail__filmstrip-play"
                      glyph={IconGlyph.PLAY}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <Button
          className="detail__filmstrip-nav web-gallery-next-arrow-pendo-tracking"
          onClick={navigateRight}
          disabled={!hasNextAsset || disableThumbnails}
          aria-label="Navigate to Next Asset"
        >
          <Icon glyph={IconGlyph.ANGLE_RIGHT} />
        </Button>
      </div>
    </div>
  );
};
